import { StatsApi, GrantInfo } from './StatsApi'
import { mockLog } from '../MockDebug'

const TAG = 'Mock Stats'

/**
 * In-memory implementation of StatsApi for playground + Storybook environments.
 * No rule eval — `submit()` always resolves with an empty `grants` array.
 * Useful for testing UI flows that read stat values without needing a real
 * backend.
 */
export class MockStatsApi implements StatsApi {
  private values = new Map<string, number>()

  async submit(statId: string, value: number): Promise<{ grants: GrantInfo[] }> {
    // Plain Error to match the production surface: the stats RPC is a batch and
    // RpcStatsApi demuxes per-stat failures into plain Errors. Messages mirror
    // server/cloud-run/src/services/stats.service.ts + stats.controller.ts.
    // Server requireNonEmptyString rejects whitespace-only too (value.trim()).
    if (typeof statId !== 'string' || statId.trim().length === 0) {
      throw new Error('statId is required')
    }
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new Error('value must be a valid number')
    }
    if (value > Number.MAX_SAFE_INTEGER || value < Number.MIN_SAFE_INTEGER) {
      throw new Error('Stat value is invalid')
    }
    mockLog(TAG, `submit ${statId}=${value}`)
    this.values.set(statId, value)
    return { grants: [] }
  }

  async getValue(statId: string): Promise<number | null> {
    return this.values.has(statId) ? this.values.get(statId) ?? null : null
  }

  async getAllValues(): Promise<Record<string, number>> {
    return Object.fromEntries(this.values)
  }
}
